import { useMemo } from 'react';
import { BlackduckData } from './types';
import { useRepositoryOnboardingContext } from '../../context/RepositoryOnboardingContext';
import { hasService } from '../../hooks/useTdmsServicesData';

// Hook that uses shared data from RepositoryOnboarding context to avoid duplicate API calls
export const useBlackduckData = (
  repositoryValue: string,
): { data: BlackduckData | null; loading: boolean } => {
  const {
    integrationData: nebulaData,
    integrationLoading,
    tdmsServices,
    tdmsLoading,
  } = useRepositoryOnboardingContext();

  // Combine loading states - we need both nebula and TDMS data
  const loading = integrationLoading || tdmsLoading;

  const data = useMemo((): BlackduckData | null => {
    if (!repositoryValue) {
      return null;
    }

    // Wait for TDMS data to load before deciding whether to show card
    if (tdmsLoading) {
      return null;
    }

    // Check if tenant has ci-cd service - if not, don't show BlackDuck card
    const hasCiCdService = hasService(tdmsServices, 'nebula-ci-cd');

    const provider = hasCiCdService ? 'nebula' : 'mercedes-benz';

    // Only proceed if we have nebula integration data
    if (!nebulaData?.BlackDuck) {
      return null;
    }

    const blackDuckIntegration = nebulaData.BlackDuck;

    // Don't show the card if we don't have any information
    if (!blackDuckIntegration.projectKey && !blackDuckIntegration.source) {
      return null;
    }

    return {
      provider: provider, // Set to nebula only when ci-cd service exists
      projectKey: blackDuckIntegration.projectKey || '',
      source: blackDuckIntegration.source || '',
    };
  }, [repositoryValue, nebulaData, tdmsServices, tdmsLoading]);

  return { data, loading };
};
